import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { FileInfo, MenuOptionValue, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { DkvTollsImport } from "./types";

export const useDkvTollImportsApi = () => {
  const api = useApi();

  const dkvTollsImportUploadCsv = useCallback(
    async (data: FormData) => {
      const url = "/admin/costs_imports/dkv_tolls";
      const response = await api.postNoResult<FormData>(url, data);
      api.mutateCache([url, `/admin/costs_imports`]);
      return response;
    },
    [api],
  );

  const useDkvTollsImport = useCallback(
    (
      query: PaginationQuery,
      signal: AbortSignal,
      importId: string,
      withErrors?: boolean,
      notExported?: boolean,
      forced?: boolean,
    ) => {
      let url =
        withErrors == undefined
          ? buildUrl(`/admin/costs_imports/dkv_tolls/${importId}`, query)
          : buildUrl(`/admin/costs_imports/dkv_tolls/${importId}/${withErrors}`, query);

      const extraParams = new URLSearchParams();
      notExported !== undefined && extraParams.set("notExported", `${notExported}`);
      forced !== undefined && extraParams.set("forced", `${forced}`);
      if (extraParams.size > 0) {
        url += (url.includes("?") ? "&" : "?") + extraParams.toString();
      }

      return useSWR<Paginated<DkvTollsImport>, CustomError, string>(url, (url) =>
        api.get<Paginated<DkvTollsImport>>({ url, signal }),
      );
    },
    [api],
  );

  const getDkvTollssFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/costs_imports/dkv_tolls/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const reloadDkvTollsLines = useCallback(
    async (id: string) => {
      const url = `/admin/costs_imports/dkv_tolls/${id}/reload`;
      await api.postNoResult(url);
      api.mutateCache([
        url,
        `/admin/costs_imports/dkv_tolls/${id}`,
        `/admin/costs_imports/dkv_tolls/${id}/true`,
        `/admin/costs_imports/dkv_tolls/${id}/false`,
      ]);
    },
    [api],
  );

  const exportExcel = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/costs_imports/dkv_tolls/${id}/export`;
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const forceCar = useCallback(
    async (id: string, carId: string, importId: string) => {
      const url = `/admin/costs_imports/dkv_tolls/${id}/row/car`;
      await api.put<{ carId: string }>(url, { carId });
      api.mutateCache([
        `/admin/costs_imports/dkv_tolls/${importId}`,
        `/admin/costs_imports/dkv_tolls/${importId}/true`,
        `/admin/costs_imports/dkv_tolls/${importId}/false`,
      ]);
    },
    [api],
  );

  const removeForcedCar = useCallback(
    async (id: string, importId: string) => {
      const url = `/admin/costs_imports/dkv_tolls/${id}/row/car/force`;
      await api.del(url);
      api.mutateCache([
        `/admin/costs_imports/dkv_tolls/${importId}`,
        `/admin/costs_imports/dkv_tolls/${importId}/true`,
        `/admin/costs_imports/dkv_tolls/${importId}/false`,
      ]);
    },
    [api],
  );

  const forceAssignment = useCallback(
    async (
      id: string,
      carId: string,
      assignmentId: string,
      assignmentType: "carDriver" | "other",
      importId: string,
    ) => {
      const url = `/admin/costs_imports/dkv_tolls/${id}/row/assignment`;
      await api.put<{ carId: string; assignmentId: string; assignmentType: string }>(url, {
        carId,
        assignmentId,
        assignmentType,
      });
      api.mutateCache([
        `/admin/costs_imports/dkv_tolls/${importId}`,
        `/admin/costs_imports/dkv_tolls/${importId}/true`,
        `/admin/costs_imports/dkv_tolls/${importId}/false`,
      ]);
    },
    [api],
  );

  const removeForcedAssignment = useCallback(
    async (id: string, carId: string, importId: string) => {
      const url = `/admin/costs_imports/dkv_tolls/${id}/row/assignment/force?carId=${carId}`;
      await api.del(url);
      api.mutateCache([
        `/admin/costs_imports/dkv_tolls/${importId}`,
        `/admin/costs_imports/dkv_tolls/${importId}/true`,
        `/admin/costs_imports/dkv_tolls/${importId}/false`,
      ]);
    },
    [api],
  );

  return {
    dkvTollsImportUploadCsv,
    exportExcel,
    reloadDkvTollsLines,
    getDkvTollssFieldFilters,
    useDkvTollsImport,
    forceCar,
    removeForcedCar,
    forceAssignment,
    removeForcedAssignment,
  };
};
